import { useEffect, useRef, useState } from 'react';
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import styles from './myProfile.module.css';
import { ContInfo } from '../../components/ContInfo/ContInfo';
import refferal from '../../assets/icons/refferal.svg';
import share from '../../assets/icons/share.svg';
import telegramIcon from '../../assets/icons/contacts/telegram.svg';
import vkIcon from '../../assets/icons/contacts/vk.svg';
import maxIcon from '../../assets/icons/contacts/max.svg';
import {
  commitMyAvatar,
  createMyAvatarUploadUrl,
  createMyProfileShareLink,
  getMyProfile,
  getSkillSuggestions,
  updateMyProfile,
  uploadAvatarFile
} from '../../api/profile';
import { getMyPriorityCases } from '../../api/caseShowcase';
import { mapApiError } from '../../utils/validators';
import { useAuth } from '../../context/AuthContext';
import {
  normalizeMaxValue,
  normalizeTelegramValue,
  normalizeVkValue,
  normalizeWebsiteValue,
} from '../../utils/contactLinks';

export const Profile = () => {
  const { refreshSession } = useAuth();
  const PRIORITY_LIST_LIMIT = 50;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [avatarState, setAvatarState] = useState({ loading: false, error: '', success: '' });
  const [shareState, setShareState] = useState({ loading: false, error: '', success: '' });
  const [inviteState, setInviteState] = useState({ error: '', success: '' });
  const [profileDraft, setProfileDraft] = useState({
    bio: '',
    city: '',
    institute: '',
    faculty: '',
    course_number: '',
    telegram: '',
    vk: '',
    max_contact: '',
    website: '',
    skills: [],
  });
  const [profileState, setProfileState] = useState({ loading: false, error: '', success: '' });
  const [wantTeamState, setWantTeamState] = useState({ loading: false, error: '' });
  const [priorityCasesState, setPriorityCasesState] = useState({ loading: true, error: '', items: [] });
  const [showProjectPopup, setShowProjectPopup] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const dragCounterRef = useRef(0);
  const [skillsQuery, setSkillsQuery] = useState('');
  const [skillsSuggestions, setSkillsSuggestions] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);

  useEffect(() => {
    async function loadProfile() {
      try {
        const [profileData, priorityResponse] = await Promise.all([
          getMyProfile(),
          getMyPriorityCases({ limit: PRIORITY_LIST_LIMIT }).catch(() => ({ items: [] })),
        ]);
        setProfile(profileData);
        setPriorityCasesState({
          loading: false,
          error: '',
          items: priorityResponse?.items || [],
        });
        setShowProjectPopup((priorityResponse?.items || []).length === 0);
        setProfileDraft({
          bio: profileData.bio || '',
          city: profileData.city || '',
          institute: profileData.institute || '',
          faculty: profileData.faculty || profileData.institute || '',
          course_number: profileData.course_number ? String(profileData.course_number) : '',
          telegram: profileData.telegram || '',
          vk: profileData.vk || '',
          max_contact: profileData.max_contact || '',
          website: profileData.website || '',
          skills: profileData.skills || [],
        });
      } catch (err) {
        setError(err.message);
        setPriorityCasesState({ loading: false, error: 'Не удалось загрузить приоритетные кейсы.', items: [] });
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, []);

  useEffect(() => {
    const query = skillsQuery.trim();
    if (!query) {
      setSkillsSuggestions([]);
      setSkillsLoading(false);
      return;
    }
    let isCancelled = false;
    const timerId = window.setTimeout(async () => {
      setSkillsLoading(true);
      try {
        const items = await getSkillSuggestions(query, 20);
        if (!isCancelled) {
          setSkillsSuggestions(Array.isArray(items) ? items : []);
        }
      } catch {
        if (!isCancelled) setSkillsSuggestions([]);
      } finally {
        if (!isCancelled) setSkillsLoading(false);
      }
    }, 250);
    return () => {
      isCancelled = true;
      window.clearTimeout(timerId);
    };
  }, [skillsQuery]);

  const addSkill = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return;
    setProfileDraft((prev) => {
      const exists = prev.skills.some((item) => item.toLowerCase() === normalized.toLowerCase());
      if (exists) return prev;
      return { ...prev, skills: [...prev.skills, normalized].slice(0, 20) };
    });
    setSkillsQuery('');
    setSkillsSuggestions([]);
  };

  const getExactSuggestedSkill = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return null;
    return skillsSuggestions.find((item) => item.name.toLowerCase() === normalized)?.name || null;
  };

  const removeSkill = (value) => {
    setProfileDraft((prev) => ({
      ...prev,
      skills: prev.skills.filter((item) => item !== value),
    }));
  };

  const handleAvatarUpload = async (file) => {
    setAvatarState({ loading: false, error: '', success: '' });
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAvatarState({ loading: false, error: 'Можно загрузить только изображение.', success: '' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarState({ loading: false, error: 'Размер файла не должен превышать 5MB.', success: '' });
      return;
    }

    try {
      setAvatarState({ loading: true, error: '', success: '' });
      setSelectedFileName(file.name || '');
      const uploadIntent = await createMyAvatarUploadUrl(file);
      await uploadAvatarFile(uploadIntent, file);
      const updatedProfile = await commitMyAvatar(uploadIntent.media_url);
      setProfile(updatedProfile);
      await refreshSession();
      setAvatarState({ loading: false, error: '', success: 'Аватар обновлён.' });
    } catch (err) {
      setAvatarState({ loading: false, error: mapApiError('profile', err), success: '' });
    }
  };

  const handleAvatarFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    await handleAvatarUpload(file);
  };

  const handleProfileSave = async (event) => {
    event.preventDefault();
    try {
      setProfileState({ loading: true, error: '', success: '' });
      const payload = {
        bio: profileDraft.bio.trim() || null,
        city: profileDraft.city.trim() || null,
        institute: profileDraft.institute.trim() || null,
        faculty: profileDraft.faculty.trim() || null,
        course_number: profileDraft.course_number ? Number(profileDraft.course_number) : null,
        telegram: normalizeTelegramValue(profileDraft.telegram),
        vk: normalizeVkValue(profileDraft.vk),
        max_contact: normalizeMaxValue(profileDraft.max_contact),
        website: normalizeWebsiteValue(profileDraft.website),
        skills: profileDraft.skills,
      };
      const updatedProfile = await updateMyProfile(payload);
      setProfile(updatedProfile);
      setProfileDraft((prev) => ({
        ...prev,
        skills: updatedProfile.skills || [],
      }));
      setProfileState({ loading: false, error: '', success: 'Профиль сохранен.' });
    } catch (err) {
      setProfileState({ loading: false, error: mapApiError('profile', err), success: '' });
    }
  };

  const handleWantTeamToggle = async (nextValue) => {
    if (!profile) return;
    setWantTeamState({ loading: true, error: '' });
    try {
      const updatedProfile = await updateMyProfile({ want_team: nextValue });
      setProfile(updatedProfile);
      setProfileDraft((prev) => ({ ...prev, want_team: Boolean(updatedProfile.want_team) }));
      setWantTeamState({ loading: false, error: '' });
    } catch (err) {
      setWantTeamState({ loading: false, error: mapApiError('profile', err) });
    }
  };

  const handleShareProfile = async () => {
    try {
      setShareState({ loading: true, error: '', success: '' });
      const payload = await createMyProfileShareLink();
      const token = payload?.token;
      if (!token) {
        throw new Error('Ссылка не была сгенерирована.');
      }
      const frontendLink = `${window.location.origin}/profiles/share/${encodeURIComponent(token)}`;
      if (navigator.share) {
        await navigator.share({
          title: 'Профиль участника',
          text: 'Ссылка на профиль участника',
          url: frontendLink,
        });
      } else {
        await navigator.clipboard.writeText(frontendLink);
      }
      setShareState({ loading: false, error: '', success: 'Ссылка на профиль готова и скопирована.' });
    } catch (err) {
      setShareState({ loading: false, error: err?.message || 'Не удалось создать ссылку.', success: '' });
    }
  };

  const handleInviteFriend = async () => {
    const refCode = profile?.login?.trim();
    if (!refCode) {
      setInviteState({ error: 'Не удалось определить реферальный код.', success: '' });
      return;
    }
    try {
      const link = `${window.location.origin}/register?ref=${encodeURIComponent(refCode)}`;
      if (navigator.share) {
        await navigator.share({
          title: 'Проектный интенсив ЮФУ.Про',
          text: `Приглашаю на проектный интенсив ЮФУ.Про. Зарегистрируйтесь по ссылке:`,
          url: link,
        });
      } else {
        await navigator.clipboard.writeText(link);
      }
      setInviteState({
        error: '',
        success: 'Ссылка-приглашение на интенсив готова и скопирована.',
      });
    } catch {
      setInviteState({ error: 'Не удалось подготовить инвайт-ссылку.', success: '' });
    }
  };

  return (
    <div>
      {showProjectPopup ? (
        <div
          className={styles.projectPopupOverlay}
          onClick={() => setShowProjectPopup(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setShowProjectPopup(false);
            }
          }}
        >
          <div className={styles.projectPopupCard} onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className={styles.projectPopupCloseBtn}
              onClick={() => setShowProjectPopup(false)}
              aria-label="Закрыть"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <h2 className={styles.projectPopupTitle}>Внимание!</h2>
            <p className={styles.projectPopupText}>
              Первым делом в «ЮФУ.Про» необходимо выбрать проектные задачи, которые вам подходят, расставив
              приоритеты.
            </p>
            <div className={styles.projectPopupButtons}>
              <Link to="/cases" className={styles.projectPopupBtn} onClick={() => setShowProjectPopup(false)}>
                Выбрать проект
              </Link>
              <button
                type="button"
                className={styles.projectPopupBtnSecondary}
                onClick={() => setShowProjectPopup(false)}
              >
                Выбрать позже
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <h1 className={styles.title}>Мой профиль</h1>
      <div className={styles.container}>
        <div className={styles.contInfo}>
          {loading ? null : (
            <>
              <ContInfo profile={profile} onWantTeamToggle={handleWantTeamToggle} wantTeamPending={wantTeamState.loading} />
              {wantTeamState.error ? <p className={styles.error}>{wantTeamState.error}</p> : null}
            </>
          )}
          {error && <p>{error}</p>}

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>Аватар профиля</h2>
            <label
              className={`${styles.dropzone} ${isDragActive ? styles.dropzoneActive : ''}`}
              onDragOver={(event) => {
                event.preventDefault();
              }}
              onDragEnter={(event) => {
                event.preventDefault();
                dragCounterRef.current += 1;
                setIsDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
                if (dragCounterRef.current === 0) {
                  setIsDragActive(false);
                }
              }}
              onDrop={async (event) => {
                event.preventDefault();
                dragCounterRef.current = 0;
                setIsDragActive(false);
                const file = event.dataTransfer?.files?.[0];
                await handleAvatarUpload(file);
              }}
            >
              <input
                className={styles.fileInput}
                type="file"
                accept="image/*"
                onChange={handleAvatarFileChange}
                disabled={avatarState.loading}
              />
              <div className={styles.dropzoneBody}>
                <span className={styles.dropzoneTitle}>
                  {avatarState.loading ? 'Загрузка аватара...' : 'Перетащи изображение сюда'}
                </span>
                <span className={styles.dropzoneHint}>
                  или нажми, чтобы выбрать файл (JPG, PNG, WEBP до 5MB)
                </span>
                {selectedFileName ? <span className={styles.dropzoneFile}>{selectedFileName}</span> : null}
              </div>
            </label>
            {avatarState.error ? <p className={styles.error}>{avatarState.error}</p> : null}
            {avatarState.success ? <p className={styles.success}>{avatarState.success}</p> : null}
          </section>

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>О себе</h2>
            <form className={styles.profileForm} onSubmit={handleProfileSave}>
              <textarea
                className={styles.textarea}
                placeholder="Коротко о себе"
                value={profileDraft.bio}
                maxLength={1000}
                onChange={(event) => {
                  setProfileDraft((prev) => ({ ...prev, bio: event.target.value }));
                  setProfileState({ loading: false, error: '', success: '' });
                }}
              />
              <input
                className={styles.input}
                type="text"
                placeholder="Город"
                value={profileDraft.city}
                maxLength={100}
                onChange={(event) => setProfileDraft((prev) => ({ ...prev, city: event.target.value }))}
              />
              <input
                className={styles.input}
                type="text"
                placeholder="Специальность"
                value={profileDraft.faculty}
                maxLength={150}
                onChange={(event) => setProfileDraft((prev) => ({ ...prev, faculty: event.target.value }))}
              />
              <input
                className={styles.input}
                type="number"
                min="1"
                placeholder="Курс"
                value={profileDraft.course_number}
                onChange={(event) => setProfileDraft((prev) => ({ ...prev, course_number: event.target.value }))}
              />
              <p className={styles.contactHintWide}>
                В контакты можно вставлять логин, <code>@username</code> или полную ссылку. Платформа сама приведет формат к рабочему виду.
              </p>
              <div className={styles.contactField}>
                <img src={telegramIcon} alt="Telegram" />
                <input
                  className={styles.input}
                  type="text"
                  placeholder="@username или t.me/username"
                  value={profileDraft.telegram}
                  maxLength={100}
                  onChange={(event) => setProfileDraft((prev) => ({ ...prev, telegram: event.target.value }))}
                />
              </div>
              <p className={styles.contactHint}>Пример: <code>@sfedupro</code> или <code>t.me/sfedupro</code></p>
              <div className={styles.contactField}>
                <img src={vkIcon} alt="VK" />
                <input
                  className={styles.input}
                  type="text"
                  placeholder="username или vk.com/username"
                  value={profileDraft.vk}
                  maxLength={255}
                  onChange={(event) => setProfileDraft((prev) => ({ ...prev, vk: event.target.value }))}
                />
              </div>
              <p className={styles.contactHint}>Пример: <code>id123456</code> или <code>vk.com/sfedupro</code></p>
              <div className={styles.contactField}>
                <img src={maxIcon} alt="MAX" />
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Ссылка-приглашение MAX"
                  value={profileDraft.max_contact}
                  maxLength={255}
                  onChange={(event) => setProfileDraft((prev) => ({ ...prev, max_contact: event.target.value }))}
                />
              </div>
              <p className={styles.contactHint}>В MAX лучше вставлять именно рабочую ссылку-приглашение или ссылку на чат/контакт.</p>
              <input
                className={styles.input}
                type="text"
                placeholder="Сайт/портфолио: example.com или https://example.com"
                value={profileDraft.website}
                maxLength={255}
                onChange={(event) => setProfileDraft((prev) => ({ ...prev, website: event.target.value }))}
              />
              <p className={styles.contactHint}>Можно вставлять сайт, портфолио, Behance, GitHub или другую публичную ссылку.</p>
              <div className={styles.skillsSelect}>
                <label className={styles.skillsLabel}>Роли и интересы</label>
                <div className={styles.skillsInputWrap}>
                  <div className={styles.skillsChips}>
                    {profileDraft.skills.map((skill) => (
                      <button
                        key={skill}
                        type="button"
                        className={styles.skillChip}
                        onClick={() => removeSkill(skill)}
                      >
                        {skill}
                        <span aria-hidden="true">×</span>
                      </button>
                    ))}
                    <input
                      className={styles.skillsInput}
                      type="text"
                      placeholder="Начните вводить роль из списка"
                      value={skillsQuery}
                      onChange={(event) => setSkillsQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ',') {
                          event.preventDefault();
                          const exactMatch = getExactSuggestedSkill(skillsQuery);
                          if (exactMatch) {
                            addSkill(exactMatch);
                          }
                        }
                        if (event.key === 'Backspace' && !skillsQuery && profileDraft.skills.length) {
                          removeSkill(profileDraft.skills[profileDraft.skills.length - 1]);
                        }
                      }}
                    />
                  </div>
                  {skillsQuery && (
                    <div className={styles.skillsDropdown}>
                      {skillsLoading ? <div className={styles.skillsHint}>Ищем теги...</div> : null}
                      {!skillsLoading && !skillsSuggestions.length ? (
                        <div className={styles.skillsHint}>Выберите роль из утвержденного списка.</div>
                      ) : null}
                      {skillsSuggestions.map((item) => (
                        <button
                          key={item.name}
                          type="button"
                          className={styles.skillsOption}
                          onClick={() => addSkill(item.name)}
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <p className={styles.muted}>
                Заполни раздел «О себе», выбери подходящие роли из списка и добавь контакты — так профиль будет
                понятнее для команд и наставников.
              </p>
              <button className={styles.transferButton} type="submit" disabled={profileState.loading}>
                {profileState.loading ? 'Сохраняем...' : 'Сохранить профиль'}
              </button>
            </form>
            {profileState.error ? <p className={styles.error}>{profileState.error}</p> : null}
            {profileState.success ? <p className={styles.success}>{profileState.success}</p> : null}
          </section>
        </div>

        <div className={styles.contButtons}>
          <Link to="/cases" className={styles.chooseProjectButton}>
            Выбрать проект
          </Link>

          <button className={styles.refferal} onClick={handleInviteFriend} type="button">
            <img src={refferal} alt="+" />
            <p>Пригласить друга</p>
          </button>

          <button className={styles.share} onClick={handleShareProfile} type="button" disabled={shareState.loading}>
            <img src={share} alt="" />
            <p>{shareState.loading ? 'Создаем ссылку...' : 'Поделиться профилем'}</p>
          </button>
          {inviteState.error ? <p className={styles.error}>{inviteState.error}</p> : null}
          {inviteState.success ? <p className={styles.success}>{inviteState.success}</p> : null}
          {shareState.error ? <p className={styles.error}>{shareState.error}</p> : null}
          {shareState.success ? <p className={styles.success}>{shareState.success}</p> : null}

          <section className={styles.priorityCasesPanel}>
            <h3 className={styles.priorityCasesTitle}>Мои выбранные кейсы</h3>
            {priorityCasesState.loading ? (
              <p className={styles.muted}>Загружаем...</p>
            ) : null}
            {priorityCasesState.error ? (
              <p className={styles.error}>{priorityCasesState.error}</p>
            ) : null}
            {!priorityCasesState.loading && !priorityCasesState.items.length ? (
              <p className={styles.muted}>Выберите приоритеты в витрине кейсов через кнопку «Подробнее».</p>
            ) : null}
            {!priorityCasesState.loading && priorityCasesState.items.length >= PRIORITY_LIST_LIMIT ? (
              <p className={styles.muted}>Показаны первые {PRIORITY_LIST_LIMIT} выбранных кейсов.</p>
            ) : null}
            {priorityCasesState.items.map((item) => (
              <Link key={item.case_id} to={`/cases?focus=${item.case_id}`} className={styles.priorityCaseRow}>
                <span className={styles.priorityCaseBadge}>П{item.user_priority ?? '-'}</span>
                <span className={styles.priorityCaseText}>
                  <strong>{item.title}</strong>
                  <small>{item.partner_name}</small>
                </span>
              </Link>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
};
